import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'
import KitchenLoader from './KitchenLoader.jsx'

// Modal for AI Schedule Optimization (priority #1 on the post-Path-B
// AI list). Mirrors the pattern of InvoiceScanModal: phases-driven
// state machine with direct fetch to the Edge Function.
//
// Flow:
//   inputs    — target week + budget config + Generate
//   generating — loading state while edge fn calls Claude
//   review    — show proposed shifts grouped by day; let manager
//               remove individual shifts they don't want
//   submitting — bulk-insert schedule_assignments
//   error     — retry path
//
// MVP scope (per Scott's call):
//   - Tier 1: basic AI proposal
//   - Both budget options ($ amount AND % of expected sales)
//   - Start from last week's schedule and adjust
export default function ScheduleSuggestModal({
  open,
  onClose,
  onAccepted,        // (count) => void  — parent refreshes its data
}) {
  const [phase, setPhase] = useState('inputs')
  const [error, setError] = useState(null)

  // Inputs
  const [weekStart, setWeekStart] = useState(() => mondayOf(addDaysToDate(new Date(), 7)))
  const [budgetType, setBudgetType] = useState('dollars') // 'dollars' | 'pct_of_sales'
  const [budgetValue, setBudgetValue] = useState('1000')
  const [replaceExisting, setReplaceExisting] = useState(false)

  // Context fetched at Generate time
  const [staffList, setStaffList] = useState([])
  const [lastWeekAssignments, setLastWeekAssignments] = useState([])
  const [lastWeekActualLabor, setLastWeekActualLabor] = useState(0)
  const [existingThisWeek, setExistingThisWeek] = useState(0) // count of shifts already on target week
  const [recentSales, setRecentSales] = useState([])
  const [contextLoading, setContextLoading] = useState(false)

  // The dollar budget actually sent to the AI. For type='dollars'
  // it's just the input value. For type='pct_of_sales' we compute
  // expected weekly sales from recent_sales (averaged by day-of-week,
  // summed across target week's open days) and apply the percentage.
  // Stored in state so we can also display the projection to the
  // manager — "Expected $14k sales × 28% = $3,920 budget" — making
  // the math transparent.
  const [targetDollars, setTargetDollars] = useState(0)
  const [expectedWeekSales, setExpectedWeekSales] = useState(0)

  // AI response
  const [proposal, setProposal] = useState(null) // { shifts, rationale, projected, warnings }
  const [removedShiftIds, setRemovedShiftIds] = useState(new Set())

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase('inputs')
      setError(null)
      setProposal(null)
      setRemovedShiftIds(new Set())
      setBudgetType('dollars')
      setBudgetValue('1000')
      setReplaceExisting(false)
      setWeekStart(mondayOf(addDaysToDate(new Date(), 7)))
    }
  }, [open])

  // When the inputs phase opens or the target week changes, fetch the
  // context (staff, last week, recent sales) so we can show the
  // manager helpful hints (last week's actual labor, existing shifts
  // already on the target week) before they hit Generate.
  useEffect(() => {
    if (!open || phase !== 'inputs') return
    let cancelled = false
    async function fetchContext() {
      setContextLoading(true)
      try {
        const lastWeekStart = addDays(weekStart, -7)
        const lastWeekEnd   = addDays(weekStart, -1)
        const salesStart    = addDays(weekStart, -28) // 4 weeks back
        const targetWeekEnd = addDays(weekStart, 6)

        const [staffRes, lastWkRes, salesRes, existingRes] = await Promise.all([
          supabase.from('staff')
            .select('id, name, role, hourly_wage')
            .eq('active', true)
            .order('name'),
          supabase.from('schedule_assignments')
            .select(`
              id, staff_id, shift_date, start_time, end_time, role, status,
              staff:staff_id(name, hourly_wage)
            `)
            .gte('shift_date', lastWeekStart)
            .lte('shift_date', lastWeekEnd),
          supabase.from('daily_sales')
            .select('sale_date, net_sales, gross_sales')
            .gte('sale_date', salesStart)
            .lte('sale_date', lastWeekEnd)
            .order('sale_date'),
          supabase.from('schedule_assignments')
            .select('id', { count: 'exact', head: true })
            .gte('shift_date', weekStart)
            .lte('shift_date', targetWeekEnd),
        ])

        if (cancelled) return
        if (staffRes.error) throw staffRes.error

        const lastWk = lastWkRes.data ?? []
        // Compute last week's actual labor cost as a hint for the budget input
        let actualLabor = 0
        for (const a of lastWk) {
          if (a.status !== 'scheduled' && a.status !== 'completed') continue
          const wage = Number(a.staff?.hourly_wage || 0)
          actualLabor += hoursBetween(a.start_time, a.end_time) * wage
        }

        setStaffList(staffRes.data ?? [])
        setLastWeekAssignments(lastWk)
        setLastWeekActualLabor(actualLabor)
        setRecentSales((salesRes.data ?? []).map((s) => ({
          ...s,
          day_of_week: dayOfWeekName(s.sale_date),
        })))
        setExistingThisWeek(existingRes.count ?? 0)

        // Pre-fill budget value with last week's actual cost as a sensible
        // default — most restaurants want to roughly match week-over-week.
        if (actualLabor > 0 && budgetType === 'dollars') {
          setBudgetValue(actualLabor.toFixed(0))
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[ScheduleSuggestModal] context fetch failed', e)
        if (!cancelled) setError('Could not load context. Try again.')
      } finally {
        if (!cancelled) setContextLoading(false)
      }
    }
    fetchContext()
    return () => { cancelled = true }
  }, [open, phase, weekStart]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Expected sales projection (powers the % of sales budget option) ----------
  // Average net_sales by day-of-week from recent_sales, then sum across
  // the target week's open days. "Open" = day-of-week with at least
  // some sales > 0 in history. Mondays with $0 across the board get
  // skipped automatically.
  useEffect(() => {
    if (!recentSales || recentSales.length === 0) {
      setExpectedWeekSales(0)
      return
    }
    const byDow = new Map()  // 0-6 → [sales]
    for (const s of recentSales) {
      const dow = parseDate(s.sale_date).getDay()
      const v = Number(s.net_sales || s.gross_sales || 0)
      if (!byDow.has(dow)) byDow.set(dow, [])
      byDow.get(dow).push(v)
    }
    const avgByDow = new Map()
    for (const [dow, values] of byDow) {
      avgByDow.set(dow, values.reduce((a, b) => a + b, 0) / values.length)
    }
    let total = 0
    for (let i = 0; i < 7; i++) {
      const dt = parseDate(addDays(weekStart, i))
      total += avgByDow.get(dt.getDay()) || 0
    }
    setExpectedWeekSales(total)
  }, [recentSales, weekStart])

  // Recompute target dollar amount whenever budget inputs change
  useEffect(() => {
    const v = Number(budgetValue) || 0
    if (budgetType === 'dollars') {
      setTargetDollars(v)
    } else {
      setTargetDollars(expectedWeekSales * (v / 100))
    }
  }, [budgetType, budgetValue, expectedWeekSales])

  // ---------- ALL useMemo hooks above the early return ----------
  // React Rules of Hooks: when `open` is false this component returns
  // null after only running the useState/useEffect hooks above. If we
  // call useMemo *after* the early return, the hook count differs
  // between open=false and open=true, React detects the mismatch on
  // the next render and unmounts the entire app (white screen).
  // Pattern lesson previously hit on DailyHuddleScreen + SettingsScreen.

  const visibleShifts = useMemo(() => {
    if (!proposal?.shifts) return []
    return proposal.shifts.filter((s) => !removedShiftIds.has(s._id))
  }, [proposal, removedShiftIds])

  const shiftsByDay = useMemo(() => {
    const map = new Map()
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i)
      map.set(d, [])
    }
    for (const s of visibleShifts) {
      if (map.has(s.shift_date)) map.get(s.shift_date).push(s)
    }
    return map
  }, [visibleShifts, weekStart])

  // Recompute totals from the still-visible (non-removed) shifts so
  // the manager sees the impact of removing rows in real time.
  const liveTotals = useMemo(() => {
    let cost = 0, hours = 0
    const staffSet = new Set()
    const wageById = new Map(staffList.map((s) => [s.id, Number(s.hourly_wage || 0)]))
    for (const s of visibleShifts) {
      const h = hoursBetween(s.start_time, s.end_time)
      const w = wageById.get(s.staff_id) || 0
      cost += h * w
      hours += h
      staffSet.add(s.staff_id)
    }
    return { cost, hours, staffCount: staffSet.size, shiftCount: visibleShifts.length }
  }, [visibleShifts, staffList])

  const staffById = useMemo(
    () => new Map(staffList.map((s) => [s.id, s])),
    [staffList]
  )

  if (!open) return null

  // ---------- Generate ---------------------------------------
  async function handleGenerate() {
    if (phase === 'generating') return
    if (staffList.length === 0) {
      setError('Add staff in Settings before generating a schedule.')
      return
    }
    if (!budgetValue || Number(budgetValue) <= 0) {
      setError('Enter a labor budget greater than zero.')
      return
    }
    setError(null)
    setPhase('generating')

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const payload = {
        week_start_date: weekStart,
        staff: staffList.map((s) => ({
          id: s.id,
          name: s.name,
          role: s.role,
          hourly_wage: Number(s.hourly_wage || 0),
        })),
        last_week_assignments: lastWeekAssignments.map((a) => ({
          staff_id:   a.staff_id,
          shift_date: a.shift_date,
          start_time: (a.start_time || '').slice(0, 5),
          end_time:   (a.end_time   || '').slice(0, 5),
          role:       a.role,
        })),
        recent_sales: recentSales.map((s) => ({
          sale_date:   s.sale_date,
          day_of_week: s.day_of_week,
          net_sales:   Number(s.net_sales || 0),
        })),
        // Always send a dollar amount — for % of sales we converted
        // it client-side using the expected-week-sales projection.
        // Simpler for the AI than estimating sales itself.
        budget: {
          type:  'dollars',
          value: Math.round(targetDollars * 100) / 100,
        },
      }

      const resp = await fetch(`${supabaseUrl}/functions/v1/suggest-schedule`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'authorization': `Bearer ${token}`,
          'apikey': anonKey,
        },
        body: JSON.stringify(payload),
      })

      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        setError(data.message || 'AI could not produce a schedule.')
        setPhase('error')
        return
      }
      // Tag each shift with a stable client-side id for remove tracking.
      const tagged = (data.shifts || []).map((s, idx) => ({
        ...s,
        _id: `s${idx}`,
      }))
      setProposal({ ...data, shifts: tagged })
      setRemovedShiftIds(new Set())
      setPhase('review')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ScheduleSuggestModal] generation failed', e)
      setError('Could not build schedule — please try again in a moment.')
      setPhase('error')
    }
  }

  // ---------- Review interactions ----------------------------
  // (visibleShifts / shiftsByDay / liveTotals declared above the
  // early return; pure helpers live here.)

  function removeShift(id) {
    setRemovedShiftIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  // ---------- Submit -----------------------------------------
  async function handleAccept() {
    if (phase === 'submitting') return
    if (visibleShifts.length === 0) {
      setError('No shifts left to log.')
      return
    }
    setPhase('submitting')
    setError(null)
    try {
      // Optionally clear existing shifts on the target week first
      // (set by the "Replace existing" toggle in the inputs phase).
      // Safe because the policies + restaurant_id filter scope this
      // to the user's restaurant.
      if (replaceExisting) {
        const targetEnd = addDays(weekStart, 6)
        const { error: delErr } = await supabase
          .from('schedule_assignments')
          .delete()
          .gte('shift_date', weekStart)
          .lte('shift_date', targetEnd)
        if (delErr) throw delErr
      }

      const inserts = visibleShifts.map((s) => ({
        staff_id:   s.staff_id,
        shift_date: s.shift_date,
        start_time: s.start_time,
        end_time:   s.end_time,
        role:       s.role || null,
        status:     'scheduled',
      }))
      const { error: insErr } = await supabase
        .from('schedule_assignments')
        .insert(inserts)
      if (insErr) throw insErr
      // Pass back the target weekStart so the parent can auto-jump
      // the visible week, plus the replaced-count for the toast.
      onAccepted?.({
        count:        inserts.length,
        weekStart,
        replaced:     replaceExisting ? existingThisWeek : 0,
      })
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ScheduleSuggestModal] accept failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
      setPhase('review')
    }
  }

  // ---------- Render -----------------------------------------
  // (staffById declared above the early return alongside other memos.)

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="AI Schedule Suggestion"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">AI Schedule Suggestion</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* ---------- Phase: INPUTS ---------- */}
          {phase === 'inputs' && (
            <>
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
                AI builds a proposed weekly schedule using your staff list, last
                week's pattern, and recent sales history. You'll review the
                proposal, remove any shifts you don't want, then accept the
                rest. Manual edits happen in the Schedule tab after.
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="ss-week">
                  Target week (Monday)
                </label>
                <input
                  id="ss-week"
                  type="date"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
                <p className="mt-1 text-xs text-black/55">
                  {prettyWeekRange(weekStart)}
                </p>
              </div>

              {/* Budget — toggle between $ amount and % of sales */}
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Labor budget
                </label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setBudgetType('dollars')}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      budgetType === 'dollars'
                        ? 'border-blue-600 bg-blue-50 text-blue-800'
                        : 'border-black/15 bg-white text-black/70'
                    }`}
                  >
                    $ amount
                  </button>
                  <button
                    type="button"
                    onClick={() => setBudgetType('pct_of_sales')}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      budgetType === 'pct_of_sales'
                        ? 'border-blue-600 bg-blue-50 text-blue-800'
                        : 'border-black/15 bg-white text-black/70'
                    }`}
                  >
                    % of sales
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={budgetValue}
                    onChange={(e) => setBudgetValue(e.target.value)}
                    placeholder={budgetType === 'dollars' ? '1000' : '28'}
                    className="w-full rounded-lg border border-black/15 px-4 py-3 text-2xl font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-base font-medium text-black/55">
                    {budgetType === 'dollars' ? 'per week' : '% of sales'}
                  </span>
                </div>
                {budgetType === 'dollars' && lastWeekActualLabor > 0 && (
                  <p className="mt-1 text-xs text-black/55">
                    Last week's actual labor: {' '}
                    <span className="font-medium tabular-nums text-black/80">
                      {formatMoney(lastWeekActualLabor)}
                    </span>
                    {' '}— a healthy target is matching or going slightly under.
                  </p>
                )}
                {budgetType === 'pct_of_sales' && expectedWeekSales > 0 && (
                  <p className="mt-1 text-xs text-black/55">
                    Expected sales this week: {' '}
                    <span className="font-medium tabular-nums text-black/80">
                      {formatMoney(expectedWeekSales)}
                    </span>
                    {' '}(based on day-of-week average over the last 4 weeks).
                    {Number(budgetValue) > 0 && (
                      <>
                        {' '}{Number(budgetValue).toFixed(1)}% target ={' '}
                        <span className="font-medium tabular-nums text-black/80">
                          {formatMoney(targetDollars)}
                        </span>
                      </>
                    )}
                  </p>
                )}
                {budgetType === 'pct_of_sales' && expectedWeekSales === 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    No sales history yet — % of sales needs imported sales
                    data. Switch to $ amount or import sales first.
                  </p>
                )}
              </div>

              {/* Context summary so manager knows what's going in */}
              <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
                <div className="mb-1 font-medium text-black/60">
                  AI will use
                </div>
                <div className="space-y-0.5 text-black/70">
                  <div>
                    <span className="font-medium tabular-nums">{staffList.length}</span> active staff
                    {staffList.length === 0 && (
                      <span className="ml-2 text-red-600">— add staff in Settings first</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium tabular-nums">{lastWeekAssignments.length}</span> shifts from last week (template)
                  </div>
                  <div>
                    <span className="font-medium tabular-nums">{recentSales.length}</span> days of sales history
                    {recentSales.length === 0 && (
                      <span className="ml-2 text-amber-700">— import sales for demand-aware suggestions</span>
                    )}
                  </div>
                </div>
              </div>

              {existingThisWeek > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                  <div>
                    <span className="font-medium">Heads up:</span>{' '}
                    {existingThisWeek} {existingThisWeek === 1 ? 'shift is' : 'shifts are'} already on this week.
                  </div>
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-amber-900">
                    <input
                      type="checkbox"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                      className="h-4 w-4 rounded border-amber-400 text-amber-700 focus:ring-amber-600"
                    />
                    <span>
                      Clear those {existingThisWeek === 1 ? 'shift' : 'shifts'} first
                      {' '}— gives a clean slate. Unchecked = AI shifts get added on top.
                    </span>
                  </label>
                </div>
              )}

            </>
          )}

          {/* ---------- Phase: GENERATING ---------- */}
          {phase === 'generating' && (
            <KitchenLoader
              messages={[
                'Reading your cover counts\u2026',
                'Lining up the stations\u2026',
                'Balancing the labor budget\u2026',
                'Plating next week\u0027s schedule\u2026',
              ]}
            />
          )}

          {/* ---------- Phase: REVIEW ---------- */}
          {phase === 'review' && proposal && (
            <>
              {/* Summary banner */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                <div className="text-[11px] font-medium text-emerald-900/70">
                  Proposed schedule
                </div>
                <div className="mt-1 text-sm leading-relaxed text-emerald-900">
                  {proposal.rationale}
                </div>

                {/* Target vs projected — color-coded by deviation */}
                {targetDollars > 0 && (() => {
                  const diff = liveTotals.cost - targetDollars
                  const pctDiff = (diff / targetDollars) * 100
                  const zone =
                    Math.abs(pctDiff) <= 5  ? 'on-target' :
                    Math.abs(pctDiff) <= 15 ? 'close'     :
                                              'off'
                  const zoneClass = {
                    'on-target': 'text-emerald-800 bg-emerald-100/70',
                    'close':     'text-amber-800   bg-amber-100/70',
                    'off':       'text-red-800     bg-red-100/70',
                  }[zone]
                  return (
                    <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${zoneClass}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span>Target</span>
                        <span className="font-medium tabular-nums">{formatMoney(targetDollars)}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <span>Projected</span>
                        <span className="font-medium tabular-nums">{formatMoney(liveTotals.cost)}</span>
                      </div>
                      <div className="mt-0.5 flex items-baseline justify-between gap-2 border-t border-current/20 pt-1">
                        <span className="font-medium text-[10px]">
                          {zone === 'on-target' ? 'On target' : zone === 'close' ? 'Close' : 'Off target'}
                        </span>
                        <span className="font-medium tabular-nums">
                          {diff >= 0 ? '+' : ''}{formatMoney(diff)} ({pctDiff >= 0 ? '+' : ''}{pctDiff.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  )
                })()}

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-emerald-900">
                  <div className="rounded-lg bg-white/70 px-2 py-1.5">
                    <div className="text-[10px] font-medium opacity-70">Total cost</div>
                    <div className="text-base font-medium tabular-nums">
                      {formatMoney(liveTotals.cost)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/70 px-2 py-1.5">
                    <div className="text-[10px] font-medium opacity-70">Total hours</div>
                    <div className="text-base font-medium tabular-nums">
                      {liveTotals.hours.toFixed(1)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/70 px-2 py-1.5">
                    <div className="text-[10px] font-medium opacity-70">Shifts</div>
                    <div className="text-base font-medium tabular-nums">
                      {liveTotals.shiftCount}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/70 px-2 py-1.5">
                    <div className="text-[10px] font-medium opacity-70">Staff</div>
                    <div className="text-base font-medium tabular-nums">
                      {liveTotals.staffCount}
                    </div>
                  </div>
                </div>

                {/* Regenerate option — back to inputs preserving values */}
                <button
                  type="button"
                  onClick={() => setPhase('inputs')}
                  className="mt-3 w-full rounded-lg border border-emerald-300 bg-white/50 px-3 py-2 text-xs font-medium text-emerald-800 hover:bg-white"
                >
                  Don't like it? Regenerate with different inputs
                </button>
              </div>

              {/* Warnings if any */}
              {proposal.warnings && proposal.warnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                  <div className="mb-1 font-medium">Heads up</div>
                  <ul className="space-y-1">
                    {proposal.warnings.map((w, i) => (
                      <li key={i} className="leading-snug">{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Per-day shift list */}
              <div className="space-y-3">
                {[...shiftsByDay.entries()].map(([date, shifts]) => (
                  <DaySection
                    key={date}
                    date={date}
                    shifts={shifts}
                    staffById={staffById}
                    onRemove={removeShift}
                  />
                ))}
              </div>

              <p className="text-xs text-black/55">
                Tap ✕ to remove a shift you don't want. Manual edits to times,
                staff, or role happen in the Schedule tab after you accept.
              </p>
            </>
          )}

          {/* ---------- Phase: SUBMITTING ---------- */}
          {phase === 'submitting' && (
            <div className="flex flex-col items-center gap-2 py-8 text-sm font-medium text-black/75">
              <svg className="h-5 w-5 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
              Saving schedule{'\u2026'}
            </div>
          )}

          {/* ---------- Phase: ERROR (with retry) ---------- */}
          {phase === 'error' && (
            <div className="space-y-3">
              <p className="text-sm text-black/70">Something went wrong. Try again?</p>
              <button
                type="button"
                onClick={() => setPhase('inputs')}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* Sticky CTA — only shows on inputs and review phases */}
        {phase === 'inputs' && (
          <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={contextLoading || staffList.length === 0}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {contextLoading ? 'Loading\u2026' : 'Generate suggestion'}
            </button>
          </div>
        )}
        {phase === 'review' && (
          <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
            <button
              type="button"
              onClick={handleAccept}
              disabled={visibleShifts.length === 0}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {visibleShifts.length === 0
                ? 'No shifts to accept'
                : `Accept ${visibleShifts.length} ${visibleShifts.length === 1 ? 'shift' : 'shifts'} \u00b7 ${formatMoney(liveTotals.cost)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------- Day section helper -----------------------------
function DaySection({ date, shifts, staffById, onRemove }) {
  const dt = parseDate(date)
  const dayName = dt.toLocaleDateString('en-US', { weekday: 'long' })
  const dayDate = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  let dayCost = 0
  for (const s of shifts) {
    const h = hoursBetween(s.start_time, s.end_time)
    const w = Number(staffById.get(s.staff_id)?.hourly_wage || 0)
    dayCost += h * w
  }
  return (
    <div className="rounded-xl border border-black/10 overflow-hidden">
      <div className="flex items-baseline justify-between border-b border-black/5 bg-black/[0.02] px-3 py-2">
        <div className="text-sm font-medium">
          {dayName}
          <span className="ml-2 text-xs font-normal text-black/55">{dayDate}</span>
        </div>
        <div className="text-xs text-black/60">
          {shifts.length === 0
            ? 'no shifts'
            : `${shifts.length} ${shifts.length === 1 ? 'shift' : 'shifts'} \u00b7 ${formatMoney(dayCost)}`}
        </div>
      </div>
      {shifts.length === 0 ? (
        <div className="px-3 py-3 text-xs italic text-black/40">
          No shifts proposed for this day.
        </div>
      ) : (
        <ul>
          {shifts.map((s) => {
            const staff = staffById.get(s.staff_id)
            const h = hoursBetween(s.start_time, s.end_time)
            const cost = h * Number(staff?.hourly_wage || 0)
            return (
              <li
                key={s._id}
                className="flex items-center gap-2 border-b border-black/5 px-3 py-2 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {staff?.name ?? '(unknown)'}
                  </div>
                  <div className="truncate text-xs text-black/60 tabular-nums">
                    {s.start_time}{'\u2013'}{s.end_time}
                    {' \u00b7 '}
                    {h.toFixed(1)} hrs
                    {s.role ? ` \u00b7 ${s.role}` : ''}
                  </div>
                </div>
                <div className="text-right text-sm font-medium tabular-nums">
                  {formatMoney(cost)}
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(s._id)}
                  aria-label="Remove this shift"
                  className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-base text-black/40 hover:bg-red-50 hover:text-red-600"
                >
                  ✕
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ---------- Date / time helpers ----------------------------
function isoDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
function parseDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}
function addDays(dateStr, n) {
  const d = parseDate(dateStr)
  d.setDate(d.getDate() + n)
  return isoDate(d)
}
function addDaysToDate(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}
function mondayOf(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return isoDate(d)
}
function hoursBetween(start, end) {
  if (!start || !end) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const startMin = sh * 60 + sm
  let endMin     = eh * 60 + em
  if (endMin < startMin) endMin += 24 * 60
  return Math.max(0, (endMin - startMin) / 60)
}
function dayOfWeekName(dateStr) {
  return parseDate(dateStr).toLocaleDateString('en-US', { weekday: 'long' })
}
function prettyWeekRange(weekStart) {
  const start = parseDate(weekStart)
  const end = parseDate(addDays(weekStart, 6))
  const fmt = (d, opts) => d.toLocaleDateString('en-US', opts)
  if (start.getMonth() === end.getMonth()) {
    return `${fmt(start, { month: 'short', day: 'numeric' })} \u2013 ${fmt(end, { day: 'numeric', year: 'numeric' })}`
  }
  return `${fmt(start, { month: 'short', day: 'numeric' })} \u2013 ${fmt(end, { month: 'short', day: 'numeric', year: 'numeric' })}`
}
